/**
 * MarketingCtaPopup — a small, dismissible call-to-action that floats bottom-
 * right on every marketing page (rendered once by MarketingLayout). Replaces
 * the per-page footer CTA bands so there's no large dead space at the foot of
 * each page. Dismissal persists in localStorage. Composes Card + Button.
 */
import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Card } from './card';
import { Button } from './button';
import { MarketingLink } from './marketing-link';

const STORAGE_KEY = 'inferrex-cta-dismissed';

export interface MarketingCtaPopupProps {
  readonly title?: string;
  readonly blurb?: string;
  readonly primaryLabel?: string;
  readonly primaryHref?: string;
  readonly secondaryLabel?: string;
  readonly secondaryHref?: string;
}

export function MarketingCtaPopup({
  title = 'Ready to connect your stack?',
  blurb = 'Every feature, unlimited users, 30-day cancellation.',
  // Gated through pricing: "Get started" routes to the pricing page (pick a
  // tier → signup), never straight to the signup/auth surface.
  primaryLabel = 'Get started',
  primaryHref = '/pricing',
  secondaryLabel = 'Talk to us',
  secondaryHref = '/contact',
}: MarketingCtaPopupProps) {
  // Start hidden; reveal after the effect checks storage (avoids a flash on reload).
  const [hidden, setHidden] = useState(true);
  useEffect(() => {
    try { setHidden(localStorage.getItem(STORAGE_KEY) === '1'); } catch { setHidden(false); }
  }, []);
  if (hidden) return null;

  const dismiss = () => {
    setHidden(true);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* private browsing */ }
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 w-80 max-w-[calc(100vw-2rem)]">
      <Card className="relative p-4 shadow-lg">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute right-2 top-2 inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
        <p className="pr-6 font-display text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{blurb}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button asChild size="sm">
            <MarketingLink href={primaryHref}>{primaryLabel}</MarketingLink>
          </Button>
          <Button asChild size="sm" variant="outline">
            <MarketingLink href={secondaryHref}>{secondaryLabel}</MarketingLink>
          </Button>
        </div>
      </Card>
    </div>
  );
}
